import { faArrowRotateLeft, faFilter } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Stack } from '@mui/material'
import { getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import AbiStatementViewApiUrls from 'src/apiUrls/AbiStatementViewApiUrls'
import { useServerPagination } from 'src/views/abi/hooks/useServerPagination'
import { createDefaultValues } from 'src/components/era-package/forms/hooks/useFormConfig'
import RenderFormSection from 'src/components/era-package/forms/RenderFormSection'
import BlankCard from 'src/components/era-package/shared/BlankCard'
import { Table } from 'src/components/era-package/Table'
import { dateRangeFilter, globalFilterFn } from 'src/utils/era-package/filters'
import { StatementDialog } from 'src/views/abi/StatementPage/dialogs'
import { monthlyStatementFilters, monthlyStmtCols, optionsMap, STATEMENT_TYPE } from '../shared'

const useDebounce = (value, delay = 500) => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debounced
}

const MONTHLY_DEFAULT_PARAMS = {
  page: 0,
  size: 10,
  search: '',
  pmsNumber: '',
  pmsImporter: '',
  pmsFiler: '',
  pmsDueDate: '',
}

const DIALOG_PROPS_RESET = {
  open: false, data: {},
  type: STATEMENT_TYPE.monthly
}

const DEFAULT_FILTERS = createDefaultValues(monthlyStatementFilters)

// Exclude these fields from being auto-mapped onto the table column filter function.
const EXCLUDE_FILTERS = ['global']

export const MonthlyTab = () => {
  const filtersForm = useMemo(() => ({
    fields: monthlyStatementFilters,
    title: 'Filters',
    icon: <FontAwesomeIcon icon={faFilter} />,
  }), [])
    const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [dialogProps, setDialogProps] = useState(DIALOG_PROPS_RESET)
  const debouncedFilters = useDebounce(filters, 500)

  const { data, totalPages, loading, params, fetchPage, updateParams } =
    useServerPagination(AbiStatementViewApiUrls.LIST_MONTHLY, MONTHLY_DEFAULT_PARAMS)
  const isMounted = useRef(false)

  const pageIndex = params.page
  const pageSize = params.size

  const table = useReactTable({
    data, columns: monthlyStmtCols,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount: totalPages,
    getRowId: row => row.pmsNumber,
    globalFilterFn, filterFromLeafRows: true,
    filterFns: { dateRange: dateRangeFilter },
    state: { pagination: { pageIndex, pageSize } },
    enableSorting: true,
    enableRowSelection: false,
    enableGlobalFilter: true,
  })

      // Filters: skip first mount run — hook auto-fetches with default params
  useEffect(() => {
    if (!isMounted.current) { isMounted.current = true; return; }
    updateParams({
      search: debouncedFilters.global ?? '',
      pmsNumber: debouncedFilters.pmsNumber ?? '',
      pmsImporter: debouncedFilters.pmsImporter ?? '',
      pmsFiler: debouncedFilters.pmsFiler ?? '',
      pmsDueDate: debouncedFilters.pmsDueDate ?? '',
      page: 0,
    })
  }, [debouncedFilters])

  const handleRowClick = (row) => {
    setDialogProps({
      open: true,
      type: STATEMENT_TYPE.monthly,
      statementNumber: row.original.pmsNumber,
      finalized: row.original.isFinalized
    })
  }

  const handleReset = () => {
    DEFAULT_FILTERS.isFinalized = true
    setFilters(DEFAULT_FILTERS)
  }

  return <Stack spacing={2}>
    <BlankCard>
      <RenderFormSection section={filtersForm}
        formData={filters} setFormData={setFilters}
        optionsMap={optionsMap} dense spacing={1.5}
        actions={<Button onClick={handleReset}
          sx={{ minWidth: 120, px: 3, py: 1 }}
          startIcon={<FontAwesomeIcon icon={faArrowRotateLeft} />}
        >
          Reset Filters
        </Button>}
      />

    </BlankCard>
    <BlankCard>
      <Table table={table} hover
        rowCount={data.length}
        onRowClick={handleRowClick}
        onRefresh={() => fetchPage()}
        loading={loading}
      />
    </BlankCard>

    <StatementDialog
      onClose={() => setDialogProps(DIALOG_PROPS_RESET)}
      {...dialogProps}
    />
  </Stack>
}